import { useEffect, useState, useCallback } from "react";
import { CurrencyDollar, TrendUp, Sparkle, Truck, GasPump, ArrowUp, ArrowDown, Play, X } from "@phosphor-icons/react";

/**
 * ROI Estimator widget for the /pricing page.
 * Sliders let a prospect enter their fleet size + weekly loads and see
 *   projected annual savings + recommended plan (drives conversions).
 * Debounces API calls at 350ms so drags don't hammer the backend.
 */
export default function ROIEstimator() {
  const API = import.meta.env?.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;
  // Optional demo video URL (Loom or YouTube embed). When unset, the "Watch demo"
  // CTA hides itself — cleaner than showing a broken placeholder button.
  const DEMO_VIDEO_URL = process.env.REACT_APP_DEMO_VIDEO_URL || "";
  const [trucks, setTrucks] = useState(3);
  const [loadsPerWeek, setLoadsPerWeek] = useState(6);
  const [hoursSaved, setHoursSaved] = useState(5);
  const [dispatcherRate, setDispatcherRate] = useState(30);
  const [extraPerLoad, setExtraPerLoad] = useState(50);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const fetchEstimate = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/billing/estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trucks, loads_per_week: loadsPerWeek,
          hours_saved_per_week: hoursSaved,
          dispatcher_hourly_rate: dispatcherRate,
          extra_per_load: extraPerLoad,
        }),
      });
      if (r.ok) setResult(await r.json());
    } catch (e) {
      // Non-fatal: widget stays showing last successful result
    } finally {
      setLoading(false);
    }
  }, [API, trucks, loadsPerWeek, hoursSaved, dispatcherRate, extraPerLoad]);

  useEffect(() => {
    const t = setTimeout(fetchEstimate, 350);
    return () => clearTimeout(t);
  }, [fetchEstimate]);

  const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;

  return (
    <section
      className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-6"
      data-testid="roi-estimator"
    >
      <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent backdrop-blur-sm p-6 sm:p-10 relative overflow-hidden">
        {/* Corner accent */}
        <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-[#FF5A1F]/8 blur-3xl pointer-events-none" />

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 flex items-center justify-center">
            <TrendUp size={20} weight="bold" className="text-[#FF5A1F]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline text-[#FF5A1F]">HOW MUCH WOULD IT SAVE YOU?</div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1">
              Drag the sliders. See your number.
            </h3>
          </div>
          {/* Live diesel price pill — the market context that makes AI negotiation matter TODAY */}
          {result?.fuel?.price_per_gallon && (
            <div
              className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 flex items-center gap-2 shrink-0"
              data-testid="roi-fuel-pill"
              title={`Source: ${result.fuel.source === "eia" ? "US Energy Information Administration weekly retail diesel avg" : "cached from EIA"}`}
            >
              <GasPump size={14} weight="fill" className="text-[#FF5A1F]" />
              <div className="text-xs">
                <span className="text-neutral-500">Today's diesel:</span>{" "}
                <b className="text-white tabular-nums">${result.fuel.price_per_gallon.toFixed(3)}/gal</b>
              </div>
              {result.fuel.wow_change_usd != null && Math.abs(result.fuel.wow_change_usd) >= 0.005 && (
                <span
                  className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${
                    result.fuel.wow_change_usd > 0 ? "text-amber-400" : "text-emerald-400"
                  }`}
                >
                  {result.fuel.wow_change_usd > 0 ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
                  ${Math.abs(result.fuel.wow_change_usd).toFixed(2)} WoW
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: sliders */}
          <div className="space-y-6">
            <Slider
              testid="roi-trucks"
              label="Fleet size"
              icon={<Truck size={16} weight="bold" />}
              value={trucks}
              min={1}
              max={25}
              step={1}
              suffix={trucks === 1 ? "truck" : "trucks"}
              onChange={setTrucks}
            />
            <Slider
              testid="roi-loads"
              label="Loads per truck per week"
              value={loadsPerWeek}
              min={1}
              max={20}
              step={1}
              suffix="loads"
              onChange={setLoadsPerWeek}
            />
            <Slider
              testid="roi-hours"
              label="Ops hours reclaimed per week"
              value={hoursSaved}
              min={0}
              max={40}
              step={0.5}
              suffix="hrs"
              onChange={setHoursSaved}
            />
            <Slider
              testid="roi-dispatcher-rate"
              label="Dispatcher hourly rate"
              value={dispatcherRate}
              min={15}
              max={100}
              step={5}
              prefix="$"
              suffix="/hr"
              onChange={setDispatcherRate}
            />
            <Slider
              testid="roi-extra-per-load"
              label="Extra $ per load from AI Rate Negotiator"
              value={extraPerLoad}
              min={0}
              max={300}
              step={10}
              prefix="$"
              suffix="/load"
              onChange={setExtraPerLoad}
            />
          </div>

          {/* Right: results */}
          <div className="space-y-4">
            {/* Hero number */}
            <div className="rounded-2xl border border-[#FF5A1F]/40 bg-gradient-to-br from-[#FF5A1F]/10 to-transparent p-6" data-testid="roi-hero-number">
              <div className="text-xs text-neutral-400 mb-1 tracking-wider">PROJECTED ANNUAL SAVINGS</div>
              <div className="font-display text-4xl sm:text-5xl font-semibold text-white tracking-tight">
                {result ? fmt(result.savings.total_savings_annual) : "—"}
              </div>
              {result?.net?.roi_multiple > 0 && (
                <div className="text-sm text-neutral-400 mt-3 flex items-center gap-1.5">
                  <Sparkle size={12} weight="fill" className="text-[#FF5A1F]" />
                  <span data-testid="roi-multiple">
                    <b className="text-white">{result.net.roi_multiple}× ROI</b>
                    <span className="text-neutral-500"> — you save {result.net.roi_multiple}× what you pay</span>
                  </span>
                </div>
              )}
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="roi-dispatcher-savings">
                <div className="text-[10px] text-neutral-500 tracking-widest mb-1">DISPATCHER TIME</div>
                <div className="font-display text-xl font-semibold">
                  {result ? fmt(result.savings.dispatcher_savings_annual) : "—"}
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">/year</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="roi-rate-savings">
                <div className="text-[10px] text-neutral-500 tracking-widest mb-1">RATE NEGOTIATION</div>
                <div className="font-display text-xl font-semibold">
                  {result ? fmt(result.savings.rate_savings_annual) : "—"}
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">/year</div>
                {result?.fuel?.price_per_gallon && result.fuel.price_per_gallon >= 4.0 && (
                  <div className="text-[10px] text-amber-400/80 mt-2 leading-tight" data-testid="roi-fuel-context">
                    Diesel is <b>${result.fuel.price_per_gallon.toFixed(2)}/gal</b> — every extra dollar
                    per load matters more today.
                  </div>
                )}
              </div>
            </div>

            {/* Recommended plan */}
            {result?.recommended_plan && (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="roi-recommended-plan">
                <div className="text-[10px] text-neutral-500 tracking-widest mb-2">RECOMMENDED PLAN</div>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-semibold text-white">
                      {result.recommended_plan.name}
                    </div>
                    <div className="text-xs text-neutral-400 mt-0.5">
                      Best fit for {result.trucks} {result.trucks === 1 ? "truck" : "trucks"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-semibold text-white">
                      ${result.recommended_plan.monthly_price}
                      <span className="text-xs text-neutral-500 font-normal">/mo</span>
                    </div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      or {fmt(result.recommended_plan.annual_price_discounted)}/yr (save 17%)
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <a
                    href="#pricing-tiers"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A1F] text-black font-medium py-2.5 text-sm hover:bg-[#FF7A3F] transition-colors"
                    data-testid="roi-cta-see-plans"
                  >
                    <CurrencyDollar size={16} weight="bold" />
                    See {result.recommended_plan.name} plan details
                  </a>
                  {DEMO_VIDEO_URL && (
                    <button
                      type="button"
                      onClick={() => setShowDemo(true)}
                      data-testid="roi-cta-watch-demo"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] text-white font-medium py-2.5 px-4 text-sm hover:bg-white/[0.06] hover:border-white/25 transition-colors"
                    >
                      <Play size={13} weight="fill" className="text-[#FF5A1F]" />
                      Watch 60s demo
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-[11px] text-neutral-500 mt-6 leading-relaxed">
          Estimates based on your inputs above. Actual savings vary — most pilots report between 6-12h/week reclaimed
          and $30-150 extra per load once they get the hang of the AI Rate Negotiator. Adjust the sliders to match your reality.
          {loading && <span className="ml-2 opacity-60">(recalculating…)</span>}
        </p>
      </div>

      {/* Demo video modal — mounted only when the URL env var is set + toggled open */}
      {showDemo && DEMO_VIDEO_URL && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setShowDemo(false)}
          data-testid="roi-demo-modal"
        >
          <div className="w-full max-w-4xl bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="text-xs text-neutral-400 tracking-wider">
                <Sparkle size={12} weight="fill" className="inline text-[#FF5A1F] mr-1.5" />
                FreightPilot AI — 60 second product tour
              </div>
              <button
                onClick={() => setShowDemo(false)}
                data-testid="roi-demo-close"
                className="text-neutral-500 hover:text-white transition-colors p-1"
              >
                <X size={16} />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <iframe
                src={DEMO_VIDEO_URL}
                title="FreightPilot AI demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


function Slider({ testid, label, icon, value, min, max, step, prefix = "", suffix = "", onChange }) {
  const displayValue = Number.isInteger(step) ? value : value.toFixed(step < 1 ? 1 : 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-2 text-sm">
        <label className="flex items-center gap-2 text-neutral-300">
          {icon}
          {label}
        </label>
        <span className="font-mono text-white font-medium tabular-nums" data-testid={`${testid}-value`}>
          {prefix}{displayValue} {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
        data-testid={testid}
        className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[#FF5A1F] bg-white/10"
        style={{
          background: `linear-gradient(to right, #FF5A1F 0%, #FF5A1F ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.1) ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.1) 100%)`,
        }}
      />
    </div>
  );
}
